import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import FeedbackToast from '../components/FeedbackToast';

const FeedbackContext = createContext(null);

const DEFAULT_DURATION = 2800;

export const FeedbackProvider = ({ children }) => {
  const [feedback, setFeedback] = useState(null);
  const timeoutRef = useRef(null);

  const dismiss = useCallback(() => {
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;

    setFeedback((current) =>
      current
        ? { ...current, closing: true }
        : null
    );

    window.setTimeout(() => {
      setFeedback(null);
    }, 180);
  }, []);

  const showFeedback = useCallback(
    ({
      type = 'success',
      title,
      message,
      duration = DEFAULT_DURATION,
    }) => {
      window.clearTimeout(timeoutRef.current);

      setFeedback({
        id: Date.now(),
        type,
        title,
        message,
        closing: false,
      });

      timeoutRef.current = window.setTimeout(
        dismiss,
        duration
      );
    },
    [dismiss]
  );

  const showSuccess = useCallback(
    (message, title = 'Tudo certo') => {
      showFeedback({
        type: 'success',
        title,
        message,
      });
    },
    [showFeedback]
  );

  const showError = useCallback(
    (message, title = 'Algo deu errado') => {
      showFeedback({
        type: 'error',
        title,
        message,
        duration: 4000,
      });
    },
    [showFeedback]
  );

  const showInfo = useCallback(
    (message, title = 'Informação') => {
      showFeedback({
        type: 'info',
        title,
        message,
      });
    },
    [showFeedback]
  );

  return (
    <FeedbackContext.Provider
      value={{
        showFeedback,
        showSuccess,
        showError,
        showInfo,
        dismissFeedback: dismiss,
      }}
    >
      {children}

      {feedback && (
        <FeedbackToast
          {...feedback}
          onClose={dismiss}
        />
      )}
    </FeedbackContext.Provider>
  );
};

FeedbackProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFeedback = () => {
  const context = useContext(FeedbackContext);

  if (!context) {
    throw new Error(
      'useFeedback deve ser usado dentro de FeedbackProvider'
    );
  }

  return context;
};